import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { accountingApi, purchaseApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * FR-ACC-04.01/02/03 — Vendor invoices with 3-way match.
 * List/detail/create/approve/pay in one page.
 */
export function VendorInvoicesPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [matchFilter, setMatchFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  const listQuery = useQuery({
    queryKey: ['ap', 'invoices', statusFilter, matchFilter],
    queryFn: () => accountingApi.listVendorInvoices({
      status: statusFilter || undefined,
      matchStatus: matchFilter || undefined,
      limit: 200,
    }),
  });

  const kpis = (() => {
    const rows = listQuery.data ?? [];
    let pendingApproval = 0, open = 0, variance = 0, totalPayable = 0;
    for (const inv of rows) {
      if (inv.status === 'pending_approval') pendingApproval++;
      if (['open', 'partially_paid'].includes(inv.status)) {
        open++;
        totalPayable += parseFloat(inv.balance_due) || 0;
      }
      if (['price_variance', 'qty_variance'].includes(inv.match_status)) variance++;
    }
    return { total: rows.length, pendingApproval, open, variance, totalPayable };
  })();

  const STATUS_OPTIONS = [
    { value: '',                 label: 'All' },
    { value: 'draft',            label: 'Draft' },
    { value: 'pending_approval', label: 'Pending approval' },
    { value: 'open',             label: 'Open' },
    { value: 'partially_paid',   label: 'Partial' },
    { value: 'paid',             label: 'Paid' },
    { value: 'rejected',         label: 'Rejected' },
    { value: 'void',             label: 'Void' },
  ];

  const MATCH_OPTIONS = [
    { value: '',                label: 'All match' },
    { value: 'matched',         label: 'Matched' },
    { value: 'price_variance',  label: 'Price var.' },
    { value: 'qty_variance',    label: 'Qty var.' },
    { value: 'resolved',        label: 'Resolved' },
    { value: 'unmatched',       label: 'Unmatched' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · Payables"
        title="Vendor invoices"
        subtitle="Three-way match (PO ↔ GRN ↔ invoice), approval workflow, and payment registration."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New invoice
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="In view" value={kpis.total} />
        <KpiTile label="Pending approval" value={kpis.pendingApproval}
          accent={kpis.pendingApproval > 0 ? 'amber' : 'default'} />
        <KpiTile label="Match variances" value={kpis.variance}
          accent={kpis.variance > 0 ? 'danger' : 'default'} />
        <KpiTile label="Total payable" value={`${fmtCurrency(kpis.totalPayable)}`} accent="finance" />
      </div>

      {/* Status chips */}
      <div className="card p-1.5 mb-3 flex gap-1 items-center overflow-x-auto border-slate-200">
        <span className="eyebrow text-slate-500 px-3 py-1 whitespace-nowrap">Status</span>
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              statusFilter === s.value
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Match chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto border-slate-200">
        <span className="eyebrow text-slate-500 px-3 py-1 whitespace-nowrap">Match</span>
        {MATCH_OPTIONS.map((m) => (
          <button
            key={m.value}
            onClick={() => setMatchFilter(m.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              matchFilter === m.value
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_34rem] gap-6">
        <div className="card overflow-hidden border-slate-200">
          {listQuery.isLoading ? <LoadingState rows={5} /> : listQuery.error ? <ErrorState error={listQuery.error} /> :
           !listQuery.data?.length ? (
            <EmptyState
              message={statusFilter || matchFilter ? 'No invoices match' : 'No vendor invoices yet'}
              hint={statusFilter || matchFilter ? 'Try clearing filters.' : 'Enter a new supplier invoice to begin.'}
              action={!(statusFilter || matchFilter) && (
                <button className="btn-primary" onClick={() => setShowCreate(true)}>
                  + New invoice
                </button>
              )}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Invoice #</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Supplier</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Match</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(listQuery.data ?? []).map((inv: any, i: number) => (
                    <tr key={inv.id} onClick={() => setSelected(inv.id)}
                        className={`cursor-pointer transition-colors ${
                          selected === inv.id
                            ? 'bg-finance-50'
                            : i % 2 ? 'bg-slate-50/40 hover:bg-slate-50' : 'bg-white hover:bg-slate-50'
                        }`}>
                      <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-900">{inv.invoice_number}</td>
                      <td className="px-4 py-2 text-sm text-slate-900">{inv.supplier_name}</td>
                      <td className="px-4 py-2"><StatusBadge status={inv.status} /></td>
                      <td className="px-4 py-2"><MatchBadge status={inv.match_status} /></td>
                      <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">
                        ${parseFloat(inv.balance_due).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px] border-slate-200">
          {!selected ? (
            <EmptyState
              message="Select an invoice"
              hint="Click a row on the left to view 3-way match detail and approval actions."
            />
          ) : (
            <VendorInvoiceDetail
              invoiceId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['ap'] })}
              onPay={() => setShowPayment(true)}
            />
          )}
        </div>
      </div>

      {showCreate && <CreateInvoiceModal onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: ['ap', 'invoices'] });
          setShowCreate(false); setSelected(id);
        }} />}

      {showPayment && selected && <PaymentModal invoiceId={selected}
        onClose={() => setShowPayment(false)}
        onRecorded={() => {
          qc.invalidateQueries({ queryKey: ['ap'] });
          setShowPayment(false);
        }} />}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = {
    draft:            'bg-slate-100 text-slate-700 ring-slate-200',
    pending_approval: 'bg-amber-50 text-amber-800 ring-amber-200',
    open:             'bg-finance-50 text-finance-800 ring-finance-200',
    partially_paid:   'bg-amber-50 text-amber-800 ring-amber-200',
    paid:             'bg-sage-50 text-sage-700 ring-sage-200',
    rejected:         'bg-red-50 text-red-700 ring-red-200',
    void:             'bg-slate-100 text-slate-500 ring-slate-200',
  }[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'finance' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-slate-200',
    finance: 'bg-finance-500',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden border-slate-200">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function MatchBadge({ status }: { status: string }) {
  const cls = {
    unmatched:       'bg-slate-100 text-slate-600 ring-slate-200',
    matched:         'bg-sage-50 text-sage-700 ring-sage-200',
    price_variance:  'bg-amber-50 text-amber-800 ring-amber-200',
    qty_variance:    'bg-red-50 text-red-700 ring-red-200',
    resolved:        'bg-finance-50 text-finance-700 ring-finance-200',
  }[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function VendorInvoiceDetail({ invoiceId, onChanged, onPay }:
    { invoiceId: string; onChanged: () => void; onPay: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['ap', 'invoice', invoiceId], queryFn: () => accountingApi.getVendorInvoice(invoiceId) });

  const [showResolve, setShowResolve] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const decideMut = useMutation({
    mutationFn: ({ approve, reason }: any) => accountingApi.decideVendorInvoice(invoiceId, approve, reason),
    onSuccess: (d) => {
      toast.success(`Invoice ${d.status}`);
      qc.invalidateQueries({ queryKey: ['ap', 'invoice', invoiceId] });
      onChanged();
      setShowReject(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const resolveMut = useMutation({
    mutationFn: (notes: string) => accountingApi.resolveInvoiceVariance(invoiceId, notes),
    onSuccess: () => {
      toast.success('Variance resolved');
      qc.invalidateQueries({ queryKey: ['ap', 'invoice', invoiceId] });
      onChanged();
      setShowResolve(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;
  const inv = q.data;
  const balance = parseFloat(inv.balance_due);
  const payable = balance > 0 && ['open','partially_paid'].includes(inv.status);

  return (
    <div>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-mono text-xs text-slate-500">Vendor invoice: {inv.invoice_number}</div>
          <div className="font-semibold">{inv.supplier_name}</div>
          <div className="text-xs text-slate-600">Due {new Date(inv.due_date).toLocaleDateString()}</div>
          {inv.po_number && <div className="text-xs text-slate-600">PO: <span className="font-mono">{inv.po_number}</span></div>}
          {inv.grn_number && <div className="text-xs text-slate-600">GRN: <span className="font-mono">{inv.grn_number}</span></div>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={inv.status} />
          <MatchBadge status={inv.match_status} />
        </div>
      </div>

      {(inv.match_status === 'price_variance' || inv.match_status === 'qty_variance') && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded text-xs mb-3">
          <div className="font-semibold">Variance detected:</div>
          <div>{inv.match_notes}</div>
          <button className="text-xs underline mt-1" onClick={() => setShowResolve(true)}>
            Resolve variance
          </button>
        </div>
      )}

      {inv.rejected_at && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded mb-3">
          Rejected {new Date(inv.rejected_at).toLocaleString()} by {inv.rejected_by_name}: {inv.rejection_reason}
        </div>
      )}

      <table className="table mb-4">
        <thead><tr><th>#</th><th>Description</th><th className="text-right">Inv qty</th><th className="text-right">Recvd</th><th className="text-right">Unit</th><th className="text-right">Total</th></tr></thead>
        <tbody>
          {inv.lines.map((l: any) => (
            <tr key={l.id}>
              <td className="text-xs">{l.line_no}</td>
              <td>
                <div className="text-xs">{l.description}</div>
                {l.product_name && <div className="font-mono text-2xs text-slate-500">{l.sku}</div>}
              </td>
              <td className="text-right">{parseFloat(l.quantity).toFixed(0)}</td>
              <td className="text-right text-xs text-slate-500">{l.po_received_qty ? parseFloat(l.po_received_qty).toFixed(0) : '—'}</td>
              <td className="text-right">{fmtCurrency(parseFloat(l.unit_price))}</td>
              <td className="text-right font-semibold">{fmtCurrency(parseFloat(l.line_total))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td colSpan={5} className="text-right text-xs text-slate-500">Subtotal</td><td className="text-right">{fmtCurrency(parseFloat(inv.subtotal))}</td></tr>
          <tr><td colSpan={5} className="text-right text-xs text-slate-500">Tax</td><td className="text-right">{fmtCurrency(parseFloat(inv.tax_total))}</td></tr>
          <tr className="font-bold"><td colSpan={5} className="text-right">Grand total</td><td className="text-right">{fmtCurrency(parseFloat(inv.grand_total))}</td></tr>
          <tr><td colSpan={5} className="text-right text-xs">Paid</td><td className="text-right">{fmtCurrency(parseFloat(inv.paid_total))}</td></tr>
          <tr className="font-bold"><td colSpan={5} className="text-right">Balance due</td><td className="text-right text-lg">{fmtCurrency(balance)}</td></tr>
        </tfoot>
      </table>

      {inv.match_variance_amount && parseFloat(inv.match_variance_amount) !== 0 && (
        <div className="text-xs text-slate-600 mb-3">
          Variance vs PO: ${parseFloat(inv.match_variance_amount).toFixed(2)}
        </div>
      )}

      {inv.payments?.length > 0 && (
        <div className="mb-3 text-xs">
          <h3 className="font-semibold text-slate-600 mb-1">Payment history</h3>
          {inv.payments.map((p: any, i: number) => (
            <div key={i}>
              {p.payment_number} ({p.method}) {new Date(p.payment_date).toLocaleDateString()}: ${parseFloat(p.applied_amount).toFixed(2)}
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-slate-200 pt-3 flex gap-2 justify-end flex-wrap">
        {inv.status === 'pending_approval' && (
          <>
            <button className="btn-secondary" disabled={decideMut.isPending}
              onClick={() => setShowReject(true)}>
              Reject
            </button>
            <button className="btn-primary" disabled={decideMut.isPending}
              onClick={() => decideMut.mutate({ approve: true })}>Approve</button>
          </>
        )}
        {payable && <button className="btn-primary" onClick={onPay}>Record payment</button>}
      </div>

      {showResolve && (
        <ResolveVarianceDialog
          loading={resolveMut.isPending}
          onClose={() => setShowResolve(false)}
          onConfirm={(notes) => resolveMut.mutate(notes)}
        />
      )}
      {showReject && (
        <RejectInvoiceDialog
          loading={decideMut.isPending}
          onClose={() => setShowReject(false)}
          onConfirm={(reason) => decideMut.mutate({ approve: false, reason })}
        />
      )}
    </div>
  );
}

function ResolveVarianceDialog({ loading, onClose, onConfirm }: { loading: boolean; onClose: () => void; onConfirm: (notes: string) => void }) {
  const [notes, setNotes] = useState('');
  return (
    <Modal
      onClose={onClose}
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Accounting · Resolve</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Resolve variance</h2>
        <p className="text-sm text-ink-500 mt-1 leading-relaxed">Provide justification for accepting the variance. This is recorded on the invoice for audit.</p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Resolution notes <span className="text-danger">*</span></label>
        <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Price variance within 2% tolerance — approved by procurement manager" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!notes.trim() || loading} onClick={() => onConfirm(notes.trim())}>
          {loading ? 'Saving…' : 'Resolve variance'}
        </button>
      </div>
    </Modal>
  );
}

function RejectInvoiceDialog({ loading, onClose, onConfirm }: { loading: boolean; onClose: () => void; onConfirm: (reason: string | undefined) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Accounting · Reject</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Reject invoice?</h2>
        <p className="text-sm text-ink-500 mt-1 leading-relaxed">The invoice moves back to draft. Your reason is visible to the submitter.</p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Duplicate invoice — already processed" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700" disabled={loading} onClick={() => onConfirm(reason.trim() || undefined)}>
          {loading ? 'Rejecting…' : 'Reject invoice'}
        </button>
      </div>
    </Modal>
  );
}

function CreateInvoiceModal({ onClose, onCreated }:
    { onClose: () => void; onCreated: (id: string) => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const in30 = (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })();

  const [mode, setMode] = useState<'three_way_match' | 'expense'>('three_way_match');
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [dueDate, setDueDate] = useState(in30);
  const [poId, setPoId] = useState('');
  const [grnId, setGrnId] = useState('');
  const [expenseAccount, setExpenseAccount] = useState('5-10-100-5000');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Array<any>>([
    { description: '', quantity: 1, unitPrice: 0, taxRate: 0 },
  ]);

  const supQuery = useQuery({ queryKey: ['suppliers'], queryFn: () => purchaseApi.listSuppliers() });
  const poQuery = useQuery({
    queryKey: ['purchase', 'orders', 'by-supplier', supplierId],
    queryFn: () => purchaseApi.listOrders({ supplierId, limit: 50 }),
    enabled: !!supplierId && mode === 'three_way_match',
  });
  const openLinesQuery = useQuery({
    queryKey: ['purchase', 'po', poId, 'open-lines'],
    queryFn: () => purchaseApi.getOpenPoLines(poId),
    enabled: !!poId && mode === 'three_way_match',
  });

  // Auto-populate lines from PO when selected
  const loadFromPo = () => {
    if (!openLinesQuery.data) return;
    setLines((openLinesQuery.data ?? []).map((l: any) => ({
      poLineId: l.po_line_id,
      productId: l.product_id,
      description: l.description,
      quantity: parseFloat(l.received_qty) || parseFloat(l.quantity),
      unitPrice: parseFloat(l.unit_price),
      taxRate: 0,
    })));
  };

  const mut = useMutation({
    mutationFn: () => accountingApi.createVendorInvoice({
      supplierId, invoiceNumber, invoiceDate, dueDate,
      mode,
      poId: mode === 'three_way_match' ? (poId || null) : null,
      grnId: mode === 'three_way_match' ? (grnId || null) : null,
      expenseAccountCode: mode === 'expense' ? expenseAccount : undefined,
      notes: notes || undefined,
      lines: lines.filter((l) => l.description.trim() && l.quantity > 0).map((l) => ({
        poLineId: l.poLineId ?? null, productId: l.productId ?? null,
        description: l.description, quantity: l.quantity,
        unitPrice: l.unitPrice, taxRate: l.taxRate,
      })),
    }),
    onSuccess: (d) => {
      if (d.matchStatus === 'matched') toast.success(`Invoice ${d.invoiceNumber}: matched, status ${d.status}`);
      else if (d.matchStatus === 'price_variance') toast(`⚠️ Price variance ${fmtCurrency(d.matchVarianceAmount)}`);
      else if (d.matchStatus === 'qty_variance') toast.error(`Qty variance: ${d.matchNotes}`);
      else toast.success(`Invoice ${d.invoiceNumber}: ${d.status}`);
      onCreated(d.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const grandTotal = lines.reduce((s, l) => {
    const sub = l.quantity * l.unitPrice;
    return s + sub + sub * (l.taxRate / 100);
  }, 0);

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Accounting · Payables</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New vendor invoice</h2>
      </div>

      <div className="px-6 pt-3 pb-2 flex-shrink-0 flex gap-4">
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="radio" checked={mode === 'three_way_match'} onChange={() => setMode('three_way_match')} />
          3-way match (PO invoice)
        </label>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="radio" checked={mode === 'expense'} onChange={() => setMode('expense')} />
          Expense (no PO)
        </label>
      </div>

      <div className="px-6 py-3 grid grid-cols-3 gap-3 flex-shrink-0">
        <div>
          <label className="label">Supplier</label>
          <select className="input" value={supplierId} onChange={(e) => { setSupplierId(e.target.value); setPoId(''); }}>
            <option value="">Select…</option>
            {supQuery.data?.map((s: any) => <option key={s.id} value={s.id}>{s.legal_name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Invoice #</label>
          <input className="input" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="vendor's own number" />
        </div>
        <div>
          <label className="label">Invoice date</label>
          <input type="date" className="input" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Due date</label>
          <input type="date" className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
        {mode === 'three_way_match' ? (
          <>
            <div>
              <label className="label">PO</label>
              <select className="input" value={poId} onChange={(e) => setPoId(e.target.value)} disabled={!supplierId}>
                <option value="">Select PO…</option>
                {poQuery.data?.filter((p: any) => ['approved','partially_received','fully_received'].includes(p.status))
                  .map((p: any) => <option key={p.id} value={p.id}>{p.po_number} (${parseFloat(p.grand_total).toFixed(2)})</option>)}
              </select>
              {poId && openLinesQuery.data && lines.length <= 1 && (
                <button className="text-xs font-semibold text-finance-700 mt-1" onClick={loadFromPo}>
                  Load {openLinesQuery.data.length} lines from PO
                </button>
              )}
            </div>
            <div>
              <label className="label">GRN <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
              <input className="input font-mono text-xs" value={grnId} onChange={(e) => setGrnId(e.target.value)} placeholder="GRN UUID" />
            </div>
          </>
        ) : (
          <div className="col-span-2">
            <label className="label">Expense account code</label>
            <input className="input font-mono" value={expenseAccount} onChange={(e) => setExpenseAccount(e.target.value)} />
          </div>
        )}
      </div>

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        <div className="border border-slate-200 rounded-xl overflow-hidden mb-3">
          <table className="table">
            <thead><tr><th>Description</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Tax %</th><th className="w-8"></th></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td><input className="input text-xs" value={l.description}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} /></td>
                  <td className="text-right"><input type="number" min={0.01} step={1} className="input w-20 text-right tabular-nums" value={l.quantity}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td className="text-right"><input type="number" min={0} step={0.01} className="input w-24 text-right tabular-nums" value={l.unitPrice}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, unitPrice: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td className="text-right"><input type="number" min={0} max={100} step={0.1} className="input w-16 text-right tabular-nums" value={l.taxRate}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, taxRate: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td>
                    <button className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                      onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="text-sm font-medium text-ink-700 hover:text-ink-900 transition-colors mb-3"
          onClick={() => setLines((prev) => [...prev, { description: '', quantity: 1, unitPrice: 0, taxRate: 0 }])}>
          + Add line
        </button>
        <div className="text-right mb-3 font-display font-bold text-xl text-ink-900 tabular-nums">
          Total: ${grandTotal.toFixed(2)}
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-slate-200 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary"
          disabled={!supplierId || !invoiceNumber || grandTotal <= 0 || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create & match'}
        </button>
      </div>
    </Modal>
  );
}

function PaymentModal({ invoiceId, onClose, onRecorded }:
    { invoiceId: string; onClose: () => void; onRecorded: () => void }) {
  const q = useQuery({ queryKey: ['ap', 'invoice', invoiceId], queryFn: () => accountingApi.getVendorInvoice(invoiceId) });
  const balance = q.data ? parseFloat(q.data.balance_due) : 0;
  const [amount, setAmount] = useState(balance);
  const [method, setMethod] = useState<'cash'|'check'|'wire'|'ach'|'card'|'other'>('wire');
  const [reference, setReference] = useState('');

  const mut = useMutation({
    mutationFn: () => accountingApi.recordVendorPayment({
      supplierId: q.data.supplier_id, method, amount, reference: reference || undefined,
      applications: [{ invoiceId, amount }],
    }),
    onSuccess: (d) => { toast.success(`Payment ${d.paymentNumber} recorded`); onRecorded(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading || !q.data) return null;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Accounting · Payment</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Pay vendor invoice</h2>
        <div className="mt-2 bg-slate-50 rounded-xl px-4 py-3 text-sm">
          <span className="font-mono font-semibold text-slate-900">{q.data.invoice_number}</span>
          <span className="text-slate-500 mx-2">·</span>
          Balance due <span className="font-display font-bold text-slate-900">{fmtCurrency(balance)}</span>
        </div>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-3">
        <div>
          <label className="label">Amount</label>
          <input type="number" min={0.01} max={balance} step={0.01}
            className="input tabular-nums" value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Method</label>
          <select className="input" value={method} onChange={(e) => setMethod(e.target.value as any)}>
            <option value="wire">Wire</option>
            <option value="ach">ACH</option>
            <option value="check">Check</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Reference <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={amount <= 0 || amount > balance + 0.01 || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Recording…' : `Pay ${fmtCurrency(amount)}`}
        </button>
      </div>
    </Modal>
  );
}
